import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import clsx from 'clsx';
import { Button } from '@/components/Button';

export type ButtonMenuItem = {
  label: string;
  description?: string;
  icon?: React.ReactNode;
  onSelect: () => void;
  disabled?: boolean;
};

type Props = {
  label: string;
  items: ButtonMenuItem[];
  iconLeft?: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost';
  /** Align the panel's edge to the trigger. Default 'right' suits Topbar actions. */
  align?: 'left' | 'right';
};

/**
 * A primary button that opens a short list of labelled choices, for when one
 * action has two or three meaningfully different variants and separate buttons
 * would imply they're unrelated.
 */
export function ButtonMenu({ label, items, iconLeft, variant = 'primary', align = 'right' }: Props) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={wrapRef}>
      <Button variant={variant} onClick={() => setOpen((o) => !o)} iconLeft={iconLeft}>
        {label}
        <ChevronDown size={13} className={clsx('transition-transform', open && 'rotate-180')} />
      </Button>

      {open && (
        <div
          role="menu"
          className={clsx(
            'absolute z-30 mt-2 w-72 overflow-hidden rounded-xl border border-border bg-surface-elevated shadow-lg',
            align === 'right' ? 'right-0' : 'left-0',
          )}
        >
          {items.map((it, i) => (
            <button
              key={it.label}
              role="menuitem"
              disabled={it.disabled}
              onClick={() => { setOpen(false); it.onSelect(); }}
              className={clsx(
                'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors',
                i > 0 && 'border-t border-border',
                it.disabled
                  ? 'cursor-not-allowed opacity-50'
                  : 'hover:bg-surface-alt',
              )}
            >
              {it.icon && (
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
                  {it.icon}
                </span>
              )}
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-text-primary">{it.label}</span>
                {it.description && (
                  <span className="mt-0.5 block text-xs leading-snug text-text-muted">{it.description}</span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
